import React, { useCallback, useMemo, useState } from 'react';
import InputArea from './InputArea';
import arrowDownBlackIcon from '../../../../assets/images/Icon_arrow_down_black.png';
import BasicButton from '../../../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../../../components/_atom/button/StyledButton';
import DivideLine from '../../../../components/_atom/line/DivideLine';
import Switch from '../../../../components/_atom/switch/Switch';
import TagBadge, { TagBadgeType } from '../TagBadge';
import CommentInput from '../../../../components/_atom/input/CommentInput';
import BasicTable from '../../../../components/_atom/table/BasicTable';
import { PagingBar } from '../../../../components/_atom/paging/PagingBar';
import toast from 'react-hot-toast';
import MethodResult from './MethodResult';
import { ConnectedWallet, Input, Kit, KitFunctionMethod, MethodInput } from '../../../../assets/constant/kit.type';
import { useSearchParams } from 'react-router-dom';
import deleteIcon from '../../../../assets/images/atom/Icon_delete_noti.png';

export type MethodFormProps = {
  method: KitFunctionMethod;
  hasTXWebhook: boolean;
  kitDetail: Kit;
  connectedWallet: ConnectedWallet;
  currentInput: MethodInput | undefined;
  inputValue: any;
  setInputValue: (value: any) => void;
  multipleInputValues: any[];
  setMultipleInputValues: (value: any[]) => void;
  result: any;
  setSelectedMethodType: (type: 'single' | 'multiple') => void;
  setIsOpenEnterPinCodeModal: (value: boolean) => void;
  password: string;
  setPassword: (value: string) => void;
  useWebhook: boolean;
  setUseWebhook: (value: boolean) => void;
  webhookUrl: string;
  setWebhookUrl: (url: string) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  runMethod: (type: 'single' | 'multiple') => Promise<void>;
};
const MethodForm = (props: MethodFormProps) => {
  const {
    method,
    hasTXWebhook,
    kitDetail,
    connectedWallet,
    currentInput,
    inputValue,
    setInputValue,
    multipleInputValues,
    setMultipleInputValues,
    result,
    setSelectedMethodType,
    setIsOpenEnterPinCodeModal,
    password,
    setPassword,
    useWebhook,
    setUseWebhook,
    webhookUrl,
    setWebhookUrl,
    loading,
    setLoading,
    runMethod,
  } = props;
  const { input_type, method_name, need_wallet, owner_only } = method;

  const parseInputValue = (inputForm: Input) => {
    const { is_metadata, valueKey, metadata_key } = inputForm;
    if (is_metadata && metadata_key) {
      return inputValue?.metadata?.[valueKey] ?? '';
    }
    return inputValue?.[valueKey] ?? '';
  };
  const setValueToInputValue = (inputForm: Input) => (value: any) => {
    const { is_metadata, valueKey, metadata_key } = inputForm;
    if (is_metadata && metadata_key) {
      setInputValue({
        ...inputValue,
        metadata: {
          ...inputValue.metadata,
          [valueKey]: value,
        },
      });
      return;
    }
    setInputValue({
      ...inputValue,
      [valueKey]: value,
    });
  };
  const addToMultipleInputValues = () => {
    setMultipleInputValues([inputValue, ...multipleInputValues]);
    const addressOptionalInput = currentInput?.required?.find((item) => item.type === 'address_optional');
    const { valueKey } = addressOptionalInput || {};
    if (valueKey) {
      const resetInputValue = { [valueKey]: inputValue[valueKey] };
      setInputValue(resetInputValue);
      return;
    }
    setInputValue({});
  };

  const [searchParams] = useSearchParams();
  const pageNum = searchParams.get(method_name) ? parseInt(searchParams.get(method_name) as string) : 1;
  const INPUT_VALUES = multipleInputValues.slice((pageNum - 1) * 10, pageNum * 10);
  const maxPageNum = multipleInputValues.length % 10 === 0 ? multipleInputValues.length / 10 : Math.floor(multipleInputValues.length / 10) + 1;

  const metadataInputs = useMemo(() => {
    const totalInputs = [...(currentInput?.required ?? []), ...(currentInput?.optional ?? [])];
    return totalInputs?.filter((item) => item.is_metadata);
  }, [currentInput]);

  const INPUT_COLGROUP = useMemo(() => {
    if (!currentInput) return [];
    const requiredColgroup = currentInput.required.map(() => 1);
    const optionalColgroup = currentInput.optional?.map(() => 1) ?? [];
    return [...requiredColgroup, ...optionalColgroup, 0.5];
  }, [currentInput]);
  const INPUT_HEADERS = useMemo(() => {
    if (!currentInput) return [];
    const requiredHeaders = currentInput.required.map((item) => ({ title: item.label, accessor: item.valueKey }));
    const optionalHeaders =
      currentInput.optional?.map((item) => ({
        title: item.label,
        accessor: item.valueKey,
      })) ?? [];
    const deleteRowHeader = {
      title: 'Delete',
      accessor: 'delete',
    };
    return [...requiredHeaders, ...optionalHeaders, deleteRowHeader];
  }, [currentInput]);
  const INPUT_RENDERER = useCallback(
    (data: any) => {
      let renderData: any = {};
      INPUT_HEADERS.map((header) => {
        if (metadataInputs?.find((metadata) => metadata.valueKey === header.accessor)) {
          if (data.metadata?.[header.accessor] instanceof File) {
            renderData[header.accessor] = data.metadata?.[header.accessor].name;
            return '';
          }
          renderData[header.accessor] = data.metadata?.[header.accessor];
          return '';
        }
        renderData[header.accessor] = data[header.accessor];
        return renderData;
      });
      renderData.delete = (
        <img
          src={deleteIcon}
          alt="delete"
          style={{ width: 16, height: 16, cursor: 'pointer', objectFit: 'contain' }}
          onClick={() => {
            const index = multipleInputValues.findIndex((item: any) => item === data);
            const newMultipleInputValues = [...multipleInputValues];
            newMultipleInputValues.splice(index, 1);
            setMultipleInputValues(newMultipleInputValues);
          }}
        />
      );
      return renderData;
    },
    [INPUT_HEADERS, metadataInputs, multipleInputValues]
  );

  const [isOpenOption, setIsOpenOption] = useState<boolean>(false);

  const isKitOwner = kitDetail?.admin_address?.toLowerCase() === connectedWallet.connectAddress?.toLowerCase();
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {need_wallet && hasTXWebhook && (
          <div
            style={{
              display: 'flex',
              marginBottom: 20,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: '20px',
            }}
          >
            Required
            <div style={{ color: '#E70707' }}>*</div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 20 }}>
          {currentInput?.required?.map((item, index) => (
            <div style={{ paddingLeft: 10 }} key={'input' + index}>
              <InputArea
                label={item.label}
                connectedWallet={connectedWallet}
                inputType={item.type}
                value={parseInputValue(item)}
                setValue={setValueToInputValue(item)}
                disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
                placeholder={item.description}
                onChange={(e) => {
                  setValueToInputValue(item)(e.target.value);
                }}
              />
            </div>
          ))}
        </div>
        {currentInput?.optional && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 20,
                gap: 9,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '20px',
                cursor: 'pointer',
              }}
              onClick={() => setIsOpenOption(!isOpenOption)}
            >
              Option
              <img
                src={arrowDownBlackIcon}
                alt="🔽"
                style={{
                  width: 12,
                  height: 8,
                  objectFit: 'contain',
                  transition: 'all 0.3s',
                  ...(isOpenOption && { transform: 'rotate(180deg)' }),
                }}
              />
            </div>
            <div
              style={{
                display: isOpenOption ? 'flex' : 'none',
                flexDirection: 'column',
                gap: 20,
                marginBottom: 20,
              }}
            >
              {currentInput?.optional.map((item: any, index: number) => {
                return (
                  <div style={{ paddingLeft: 10 }} key={'input_optional' + index}>
                    <InputArea
                      label={item.label}
                      connectedWallet={connectedWallet}
                      inputType={item.type}
                      value={parseInputValue(item)}
                      setValue={setValueToInputValue(item)}
                      disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
                      placeholder={item.description}
                      onChange={(e) => {
                        setValueToInputValue(item)(e.target.value);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
        {input_type === 'multiple' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0 20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <BasicButton
                theme={ButtonTheme.NORMAL}
                style={{ width: 'fit-content', height: 'fit-content', padding: '7px 13px' }}
                onClick={addToMultipleInputValues}
                disabled={
                  !currentInput?.required?.some((item) => {
                    if (inputValue.metadata) {
                      return inputValue.metadata[item.valueKey];
                    }
                    return inputValue[item.valueKey];
                  }) ||
                  (connectedWallet.connectWalletType === 'metamask' && multipleInputValues.length >= 30)
                }
              >
                + Add to List
              </BasicButton>
              {connectedWallet.connectWalletType === 'metamask' && multipleInputValues.length >= 30 && (
                <div style={{ fontSize: 14, lineHeight: '16px' }}>You can mint up to 30 tokens at once when using metamask.</div>
              )}
            </div>
          </div>
        )}
      </div>
      {need_wallet && hasTXWebhook && (
        <>
          <DivideLine style={{ margin: '20px 0' }} />
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '20px',
              }}
            >
              Setting
            </div>
            <div>
              <div className="kit-label-value-wrapper" style={{ alignItems: 'center', marginTop: 20 }}>
                <div className="kit-label" style={{ minWidth: 'fit-content' }}>
                  Webhook (Transaction)
                </div>
                <Switch isOn={useWebhook} onToggle={() => setUseWebhook(!useWebhook)} />
                <TagBadge type={useWebhook ? TagBadgeType.BLUE : TagBadgeType.GREY} text={'Webhook Manager'} />
              </div>
              {useWebhook && (
                <div style={{ paddingLeft: 10, marginTop: 4 }}>
                  <CommentInput
                    value={webhookUrl}
                    disabled={kitDetail?.status !== 4 || (owner_only && !isKitOwner)}
                    placeholder={'Enter webhook url you want to receive event'}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {multipleInputValues.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20 }}>
          <BasicButton
            theme={ButtonTheme.NORMAL}
            style={{
              alignSelf: 'flex-end',
              width: 'fit-content',
              height: 'fit-content',
              padding: '7px 13px',
            }}
            onClick={() => setMultipleInputValues([])}
          >
            Reset
          </BasicButton>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              width: '100%',
            }}
          >
            <BasicTable data={INPUT_VALUES} colGroup={INPUT_COLGROUP} headers={INPUT_HEADERS} renderer={INPUT_RENDERER} />
            <PagingBar maxPageNum={maxPageNum} query={method_name} />
          </div>
          <BasicButton
            theme={ButtonTheme.PRIMARY}
            style={{
              alignSelf: 'flex-end',
              width: 'fit-content',
              height: 'fit-content',
              padding: '7px 13px',
            }}
            onClick={() => {
              setSelectedMethodType('multiple');
              if (need_wallet && connectedWallet.connectAddress === '') {
                toast.error('Please connect wallet first');
                return;
              }
              if (need_wallet && password.length !== 6 && connectedWallet.connectWalletType === 'accountManager') {
                setIsOpenEnterPinCodeModal(true);
                return;
              }
              runMethod('multiple').finally(() => {
                setPassword('');
                setLoading(false);
              });
            }}
          >
            Send All({multipleInputValues.length})
          </BasicButton>
        </div>
      )}
      {kitDetail?.status === 4 && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <BasicButton
            size={ButtonSize.DYNAMIC}
            style={{ width: 200, height: 40 }}
            theme={ButtonTheme.PRIMARY}
            onClick={async () => {
              setSelectedMethodType('single');
              if (need_wallet && connectedWallet.connectAddress === '') {
                toast.error('Please connect wallet first');
                return;
              }
              if (need_wallet && password.length !== 6 && connectedWallet.connectWalletType === 'accountManager') {
                setIsOpenEnterPinCodeModal(true);
                return;
              }
              runMethod('single').finally(() => {
                setPassword('');
                setLoading(false);
              });
            }}
            disabled={(owner_only && !isKitOwner) || loading}
          >
            Send
          </BasicButton>
        </div>
      )}
      {kitDetail?.status === 4 && result && (
        <div style={{ marginTop: 30 }}>
          <MethodResult method={method} result={result} kitDetail={kitDetail} />
        </div>
      )}
    </div>
  );
};

export default MethodForm;
